import React, { useState, useEffect } from "react";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { LicenceCheckingService } from "services/LicenceCheckingService";
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { Intent } from '@blueprintjs/core';
import PropTypes from 'prop-types';
import moment from "moment";
import { LinkButton } from "components/buttons";
import { ShowHide } from "components/layout";
import { InstantReportButton } from "components/reporting";
import { Dropdown } from "components/dropdown";
import { DateToLocal } from "components/dates";
import { Link } from "react-router-dom";
import { ListingTable } from "components/listing";
import { FormDateRangeInput } from "components/form-fields";
import { Numeric } from "components/formatting";


export function CreditHistory(
    {
        summary = false
    }
) {

    const requiredAction = "LicenceChecking:Manage";
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState(null);
    const [tableRecordCount, setTableRecordCount] = useState(0);
    const noDataMessage = "No credit information for the selected date period";
    const dateTimeFormat = "DD MMM YYYY HH:mm";
    const dateFormat = "YYYY-MM-DD";
    const badRequestCode = 400;
    const [reportGenerationId, setReportGenerationId] = useState(null);
    const [reportList, setReportList] = useState([]);
    const [showReportList, setShowReportList] = useState(false);
    const summaryRecordsPerPage = 25;
    const fullRecordsPerPage = 100;
    const recordsPerPage = summary ? summaryRecordsPerPage : fullRecordsPerPage;
    const tableHeaders = ["Type", "Check Reason", "Detail", "Employee Name", "Completed By", "Credit Amount", "Date"];

    const [tableOptions, setTableOptions] = useState({
        startDate: moment().startOf('month').format(dateFormat),
        endDate: moment().format(dateFormat),
        pageNumber: 1,
        pageSize: recordsPerPage
    });

    useEffect(onTableChange, [tableOptions]);

    function onTableChange() {
        setLoading(true);

        var errorHandling = function (error) {
            if (error.status === badRequestCode) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Failed to get credits history. Please refresh the page.");
            }
            setTableData([]);
            setTableRecordCount(0);
        }

        LicenceCheckingService.getCreditsHistory(tableOptions.pageSize, tableOptions.pageNumber, tableOptions.startDate, tableOptions.endDate).then(r => {

            let historyData = r.data.map((h) => {
                let colourClass = h.creditAmount < 0 ? "text-intent-danger" : "text-intent-success";
                let creditSymbol = h.creditAmount > 0 ? "+" : "";
                return (
                    [  
                        h.creditType,
                        h.checkType,
                        h.checkTypeDetail,
                        h.employeeName,
                        h.completedBy,
                        <div key={`${h.employeeName}-${h.date}`} className={colourClass}>{creditSymbol}<Numeric>{h.creditAmount}</Numeric></div>,
                        <DateToLocal key={`${h.employeeName}-${h.date}-Date`} inputFormat={dateTimeFormat}>{moment.utc(h.date)}</DateToLocal>
                    ]
                );
            });

            setTableData(historyData);
            setTableRecordCount(r.totalCount);
        }, errorHandling).catch(errorHandling)
            .finally(() => {
                setLoading(false);
            });
    }

    function generateCreditReport() {
        LicenceCheckingService.generateCreditReport(tableOptions.startDate, tableOptions.endDate).then(function (response) {
            setReportGenerationId(response);
        }, function () {
            setReportGenerationId(null);
            NotificationToaster.show(Intent.DANGER, "Failed to generate report. Please try again.");
        });
    }

    function generateCreditReportComplete() {
        setReportGenerationId(null);
    }

    function onDateSelect(dates) {

        if (dates.startDate.dateString != null && dates.endDate.dateString != null) {
            setTableOptions((prev) => {
                return {
                    ...prev,
                    startDate: dates.startDate.dateString,
                    endDate: dates.endDate.dateString
                }
            });
        }

    }

    function onTablePageChange(page) {
        setTableOptions((prev) => {
            return {
                ...prev,
                pageNumber: page,
            }
        });
    }



    useEffect(function () {

        setReportList([
            <InstantReportButton
                buttonType='MenuItem'
                icon={<i className='fa fa-file-excel-o'></i>}
                key={"credit-report"}
                text={"Licence Checking Credit History"}
                onReportGenerate={generateCreditReport}
                onReportDownloadComplete={generateCreditReportComplete}
                reportId={reportGenerationId}
            />
        ]);

        setTimeout(() => {
            setShowReportList(false);
        });
    }, [reportGenerationId]);


    function onReportDropdownClick() {
        setShowReportList((prev) => !prev);
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <>
                    <h2>{summary ? "Credit History for the Current Month" : "Credit History"}</h2>
                    <div className="spacer-bottom inline-items">
                        <ShowHide
                            evaluator={summary}
                            hide={(
                                <FormDateRangeInput
                                    startDate={tableOptions.startDate}
                                    endDate={tableOptions.endDate}
                                    allowClear={false}
                                    onChange={onDateSelect}
                                    disabled={loading}
                                />
                            )}
                        />
                        <Dropdown
                            isOpen={showReportList}
                            onClick={onReportDropdownClick}
                            intent="primary"
                            text={""}
                            icon={"document"}
                            large={summary}
                            iconOnly
                            id="reports"
                            items={reportList}
                            preamble={(
                                <div>
                                    <h4>Downloads</h4>
                                    {summary ? <p>Reports downloaded from this page will only include data from the current month (<DateToLocal format="DD MMM YYYY">{tableOptions.startDate}</DateToLocal> - <DateToLocal format="DD MMM YYYY">{tableOptions.endDate}</DateToLocal>). For full range reporting visit <Link to="/licence-checking/settings/credit-history">Full Credit History</Link>.</p> : <p>Reports downloaded from this page will only include data from the selected date range.</p>}
                                </div>
                            )}
                            disabled={false}
                        />
                        <ShowHide
                            evaluator={summary}
                            show={(
                                <LinkButton intent="primary" large={false} text="Full credit history" href="/licence-checking/settings/credit-history" id="full-credit-history" />
                            )}
                        />
                    </div>

                    <NotificationInline intent="info" text="Your report has been queued, and will download as soon as it's ready." show={reportGenerationId != null} />
                    <ListingTable
                        id="listing-table-check-history"
                        noDataMessage={noDataMessage}
                        sortable={false}
                        headers={tableHeaders}
                        data={tableData}
                        totalRecordCount={tableRecordCount}
                        pageable
                        showPerPage={false}
                        pageSize={recordsPerPage}
                        loadingData={loading}
                        onPagingChange={onTablePageChange}
                    />
                </>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}

CreditHistory.propTypes = {
    summary: PropTypes.bool
};